/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import IconMenuButton from 'apollo-react/components/IconMenuButton';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import EllipsisVertical from 'apollo-react-icons/EllipsisVertical';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SortableTree, {
  addNodeUnderParent,
  changeNodeAtPath,
  removeNodeAtPath,
  toggleExpandedForAll,
} from 'react-sortable-tree';
import 'react-sortable-tree/style.css';
import FileExplorerTheme from 'react-sortable-tree-theme-full-node-drag';
import { v4 as uuidv4 } from 'uuid';

import { updateRepositoryTree } from '../../actions/RepositoryConfigurationActions';
import triangleDown from '../../assets/icons/triangle-down.svg';
import triangleRight from '../../assets/icons/triangle-right.svg';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { equalityFn } from '../../form-designer/utils/form-manager.utils';
import {
  addSavedSearch,
  copySavedSearch,
  deleteSavedSearchNode,
  setSelectedSavedSearchId,
} from '../../saved-search-designer/actions/editor.actions';
import { getSelectedSavedSearchId } from '../../saved-search-designer/selectors/editor.selectors';
import { getRepositoryTree } from '../../selectors/RepositoryConfigurationSelectors';

const useStyles = makeStyles({
  tree: {
    "& [class*='rstcustom__rowContents']": {
      backgroundColor: 'rgba(35, 114, 253, 0.07)',
      border: 'none',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: 'rgba(35, 114, 253, 0.2)',
      },
    },
    "& [class*='rst__lineHalfHorizontalRight']": {
      '&::before': {
        backgroundColor: 'rgba(35, 114, 253, 0.2)',
        height: '2px',
      },
    },
    "& [class*='rst__lineHalfVerticalTop']": {
      '&::after': {
        backgroundColor: 'rgba(35, 114, 253, 0.2)',
        width: '2px',
      },
    },
    "& [class*='rst__lineHalfVerticalBottom']": {
      '&::after': {
        backgroundColor: 'rgba(35, 114, 253, 0.2)',
        width: '2px',
      },
    },
    "& [class*='rst__lineFullVertical']": {
      '&::after': {
        backgroundColor: 'rgba(35, 114, 253, 0.2)',
        width: '2px',
      },
    },
    "& [class*='rstcustom__lineChildren']": {
      marginLeft: '-10px !important',
      display: 'none !important',
    },
    "& [class*='rstcustom__rowWrapper']": {
      padding: '5px 5px 5px 0px',
      cursor: 'pointer',
    },
    "& [class*='rstcustom__rowLabel']": {
      flex: 1,
      padding: 0,
    },
    "& [class*='rstcustom__expandButton']": {
      background: `url(${triangleRight})`,
      backgroundColor: '#fff',
      backgroundRepeat: 'no-repeat',
      borderRadius: 0,
      boxShadow: 'none',
      cursor: 'pointer',
      left: '-19px !important',
      '&:hover': {
        backgroundSize: '6px',
        height: '16px',
        width: '16px',
      },
    },
    "& [class*='rstcustom__collapseButton']": {
      background: `url(${triangleDown})`,
      backgroundColor: '#fff',
      backgroundRepeat: 'no-repeat',
      borderRadius: 0,
      boxShadow: 'none',
      cursor: 'pointer',
      left: '-19px !important',
      '&:hover': {
        backgroundSize: '12px',
        height: '16px',
        width: '16px',
      },
    },
  },
  actionIcon: {
    width: 16,
    height: 16,
  },
  selectedRow: {
    "& [class*='rstcustom__rowContents']": {
      backgroundColor: 'rgba(35, 114, 253, 0.2)',
      fontFamily: 'bold',
    },
  },
  buttonsContainer: {
    marginTop: 5,
    marginRight: 5,
    display: 'flex',
    justifyContent: 'end',
  },
  singleRoot: {
    "& [class*='rst__nodeContent']": {
      marginLeft: '-29px !important',
    },
    "& [class*='rst__lineHalfHorizontalRight']": {
      '&::before': {
        display: 'none',
      },
    },
  },
  nodeContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer',
    alignItems: 'center',
  },
  nodeNameInput: {
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    fontSize: '14px',
    minWidth: '250px',
  },
  nodeText: {
    cursor: 'pointer',
    fontSize: 14,
    marginRight: 20,
  },
  selectedNodeText: {
    fontWeight: 'bold',
  },
});

const NodeRow = ({ node, path, updateTreeData, isSelectedNode, isDeleteNodeDisabled }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const inputRef = useRef();

  const [rowName, setRowName] = useState(node.nodeText);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setRowName(node.nodeText);
  }, [node.nodeText]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const handleNameBlur = () => {
    const newName = rowName.trim();

    setIsEditing(false);
    setRowName(newName);

    if (rowName !== node.nodeText) {
      updateTreeData((tree) =>
        changeNodeAtPath({
          treeData: tree,
          path,
          getNodeKey: ({ treeIndex }) => treeIndex,
          newNode: { ...node, nodeText: newName },
        })
      );
    }
  };

  const deleteNode = () => {
    dispatch(deleteSavedSearchNode({ savedSearchId: node.nodeId }));
    updateTreeData((tree) => {
      const updatedTree = removeNodeAtPath({
        treeData: tree,
        path,
        getNodeKey: ({ treeIndex }) => treeIndex,
      });

      const firstRootFolderId = updatedTree[0]?.nodeId;
      dispatch(setSelectedSavedSearchId(firstRootFolderId));

      return updatedTree;
    });
  };

  const addChildNode = () => {
    const newNodeId = `new_${uuidv4()}`;

    dispatch(addSavedSearch({ newSavedSearchId: newNodeId }));
    updateTreeData(
      (tree) =>
        addNodeUnderParent({
          treeData: tree,
          parentKey: path[path.length - 1],
          getNodeKey: ({ treeIndex }) => treeIndex,
          newNode: { nodeText: 'New sub folder', expanded: true, nodeId: newNodeId, children: [] },
        }).treeData
    );
  };

  const copyNode = () => {
    const parentIndex = path.length === 0 ? path.length - 1 : path.length - 2;

    const newNodeId = `new_${uuidv4()}`;

    dispatch(copySavedSearch({ newSavedSearchId: newNodeId, originalSavedSearchId: node.nodeId }));
    updateTreeData(
      (tree) =>
        addNodeUnderParent({
          treeData: tree,
          parentKey: path[parentIndex],
          getNodeKey: ({ treeIndex }) => treeIndex,
          newNode: {
            nodeText: `Copy of ${node.nodeText}`,
            nodeId: newNodeId,
            expanded: true,
            children: [],
          },
        }).treeData
    );
  };

  const menuItems = [
    {
      text: 'Add sub folder',
      onClick: addChildNode,
    },
    {
      text: 'Edit Name',
      onClick: () => setIsEditing(true),
    },
    {
      text: 'Copy folder',
      onClick: copyNode,
    },
    {
      text: 'Delete folder',
      onClick: deleteNode,
      destructiveAction: true,
      disabled: isDeleteNodeDisabled(node.nodeId),
    },
  ];

  const handleClick = (e) => {
    const isFolderName = e.target.getAttribute('ui-ta') === 'Folder Name';

    if (e.currentTarget === e.target || isFolderName) {
      dispatch(setSelectedSavedSearchId(node.nodeId));
    }
  };

  return (
    <div className={classes.nodeContainer} onClick={handleClick}>
      {isEditing ? (
        <input
          type="text"
          value={rowName}
          onChange={(e) => setRowName(e.target.value)}
          onBlur={handleNameBlur}
          ref={inputRef}
          className={classes.nodeNameInput}
        />
      ) : (
        <Typography
          className={classNames(classes.nodeText, isSelectedNode && classes.selectedNodeText)}
          ui-ta="Folder Name"
        >
          {node.nodeText}
        </Typography>
      )}
      <Tooltip title="Actions" disableFocusListener key="actions">
        <IconMenuButton id="actions" menuItems={menuItems} size="small">
          <EllipsisVertical className={classes.actionIcon} />
        </IconMenuButton>
      </Tooltip>
    </div>
  );
};

const RepositoryTree = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const repositoryTree = useSelector(getRepositoryTree, equalityFn) || [];
  const selectedSavedSearchId = useSelector(getSelectedSavedSearchId);

  const handleTreeChange = (newTree) => {
    dispatch(updateRepositoryTree(newTree));
  };

  const handleTreeAction = (updateFn) => {
    const updatedTree = updateFn(repositoryTree);
    dispatch(updateRepositoryTree(updatedTree));
  };

  const toggleExpanded = (flag) => {
    const newTree = toggleExpandedForAll({ treeData: repositoryTree, expanded: flag });
    dispatch(updateRepositoryTree(newTree));
  };

  const isSingleRootWithoutChildren =
    repositoryTree.length === 1 && !repositoryTree[0].children.length;

  const isDeleteNodeDisabled = (nodeId) =>
    repositoryTree.length === 1 && repositoryTree.find((node) => node.nodeId === nodeId);

  return (
    <>
      <div className={classes.buttonsContainer}>
        <Button
          size="small"
          style={{ marginRight: 10 }}
          variant="secondary"
          onClick={() => toggleExpanded(true)}
        >
          {translateString('Expand all')}
        </Button>
        <Button size="small" variant="secondary" onClick={() => toggleExpanded(false)}>
          {translateString('Collapse all')}
        </Button>
      </div>
      <div style={{ height: '100%' }}>
        <SortableTree
          treeData={repositoryTree}
          theme={FileExplorerTheme}
          onChange={handleTreeChange}
          canDrag
          canDrop={() => true}
          isVirtualized={false}
          rowHeight={45}
          className={classNames(classes.tree, isSingleRootWithoutChildren && classes.singleRoot)}
          slideRegionSize={200}
          isNodeExpanded={() => true}
          generateNodeProps={({ node, path }) => ({
            className: node.nodeId === selectedSavedSearchId ? classes.selectedRow : null,
            title: (
              <NodeRow
                node={node}
                path={path}
                updateTreeData={handleTreeAction}
                isSelectedNode={node.nodeId === selectedSavedSearchId}
                isDeleteNodeDisabled={isDeleteNodeDisabled}
              />
            ),
          })}
        />
      </div>
    </>
  );
};

export default React.memo(RepositoryTree);
